import type {
    NavigationItem,
    NavigationItemExternalLink,
    NavigationItemLink,
    NavigationItemLinkType
} from '@/types/aggregation/common';
import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';
import { ROUTES } from '@/utils/constants';
import { NAVIGATION_ITEM_TYPE_LIVESTREAM } from '@/utils/constants/navigation';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { removeProductFromUid } from '@/utils/url/url';
import { NEW_CATEGORY_UIDS } from '@/app/(pages)/(category)/c/[slug]/constants';

export type NavigationItemUI = {
    name: string;
    pathname: string | null;
    customImage: {
        name: string;
        url: string;
    };
    customImageActive: {
        name: string;
        url: string;
    };
    type?: string;
    linkUID?: string;
    linkType?: NavigationItemLinkType;
    isHighlighted?: boolean;
    newItemsCount?: number;
    pinned: boolean;
    isPersonalized: boolean;
};

export const getPathFromLink = (link: NavigationItemLink) => {
    switch (link.type) {
        case 'static_page':
            return `${ROUTES.STATIC_PAGE}${removeProductFromUid(link.uid)}`;
        case 'category':
            return `${ROUTES.CATEGORY}${removeProductFromUid(link.uid)}`;
        case 'homepage':
            return ROUTES.HOMEPAGE;
        case 'game':
        case 'game_lobby':
            return `${ROUTES.GAME}${link.uid}`;
        case 'dynamic_route':
            return `${ROUTES.DYNAMIC_PAGE}${removeProductFromUid(link.uid)}`;
        default:
            return null;
    }
};

export const getPathFromExternalLink = (externalLink: NavigationItemExternalLink | undefined) => {
    if (!externalLink) {
        return null;
    }
    switch (externalLink.link_type) {
        case 'Document':
            return getPathFromLink(externalLink);
        case 'Web':
        case 'Media':
            return externalLink.url;
        default:
            return null;
    }
};

export const getPathFromNavigationItem = (item: NavigationItem) => {
    if (item.item_type?.toLowerCase() === NAVIGATION_ITEM_TYPE_LIVESTREAM) {
        return ROUTES.LIVESTREAM;
    }

    return getPathFromLink(item.link) || getPathFromExternalLink(item.external_link);
};

export const adaptNavigationItems = (items: NavigationItem[], isPersonalizedNav = false): NavigationItemUI[] => {
    return items
        .map((item) => ({
            name: item.name,
            pathname: getPathFromNavigationItem(item),
            linkUID: item.link?.uid,
            linkType: item.link?.type,
            isHighlighted: item.is_highlighted,
            type: item.item_type,
            customImage: {
                name: item.custom_image?.name ?? '',
                url: item.custom_image?.url ?? ''
            },
            customImageActive: {
                name: item.custom_image_active?.name ?? '',
                url: item.custom_image_active?.url ?? ''
            },
            newItemsCount: item.new_items_count,
            pinned: Boolean(item.pinned),
            isPersonalized: isPersonalizedNav
        }))
        .filter((link) => link.pathname)
        .filter((link, index, links) => links.findIndex((candidate) => candidate.pathname === link.pathname) === index);
};

export const adaptTopNavigation = (
    items: NavigationItem[],
    siteWideJackpot: SiteWideJackpotData | undefined,
    isPersonalizedNav = false,
    productFeatureNames: string[] = [],
    bottomBarItems: NavigationItem[] = []
): NavigationItemUI[] => {
    const mappedItems = adaptNavigationItems(items, isPersonalizedNav);
    const deduplicatedItems = mappedItems.filter((item) => {
        const uid = item.linkUID ?? '';
        return (
            NEW_CATEGORY_UIDS.includes(uid) ||
            uid === 'casino-refer' ||
            !bottomBarItems.some(
                (bottomItem) =>
                    bottomItem.link.uid === uid ||
                    (item.pathname !== null && getPathFromNavigationItem(bottomItem) === item.pathname)
            )
        );
    });
    const hasLivestream = productFeatureNames.includes(PRODUCT_THROTTLES.HAS_LIVESTREAM);
    const visibleItems = hasLivestream
        ? deduplicatedItems
        : deduplicatedItems.filter((item) => item.type?.toLowerCase() !== NAVIGATION_ITEM_TYPE_LIVESTREAM);

    if (siteWideJackpot?.enabled === true) {
        return visibleItems;
    }

    const jackpotCategoryUids = new Set(
        (siteWideJackpot?.jackpot_categories ?? [])
            .map((item: { category?: { uid?: string }; uid?: string }) => item.category?.uid ?? item.uid)
            .filter((uid): uid is string => Boolean(uid))
            .map((uid) => removeProductFromUid(uid))
    );

    if (jackpotCategoryUids.size === 0) {
        return visibleItems;
    }

    return visibleItems.filter((item) => {
        if (item.linkType !== 'category' || !item.linkUID) {
            return true;
        }
        return !jackpotCategoryUids.has(removeProductFromUid(item.linkUID));
    });
};
